import React from 'react';
import { motion } from 'framer-motion';
type HeroVariant = 'default' | 'centered' | 'immersive';
interface PageHeroProps {
  title: string;
  subtitle?: string;
  label?: string;
  variant?: HeroVariant;
  backgroundImage?: string;
  children?: React.ReactNode;
  icon?: React.ReactNode;
}
export function PageHero({
  title,
  subtitle,
  label,
  variant = 'default',
  backgroundImage,
  children,
  icon
}: PageHeroProps) {
  if (variant === 'immersive') {
    return <section className="relative pt-36 md:pt-44 pb-16 md:pb-24 min-h-[55vh] flex items-center justify-center overflow-hidden" data-id="element-151">
        <div className="absolute inset-0 bg-gradient-to-br from-navy via-navy-light to-navy" data-id="element-152" />
        {backgroundImage && <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{
        backgroundImage: `url(${backgroundImage})`
      }} data-id="element-153" />}
        <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: 'radial-gradient(circle at 30% 50%, rgba(196,164,100,0.4) 0%, transparent 60%), radial-gradient(circle at 70% 80%, rgba(196,164,100,0.2) 0%, transparent 50%)'
      }} data-id="element-154" />

        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto" data-id="element-155">
          {icon && <motion.div initial={{
          opacity: 0,
          scale: 0.5
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.5
        }} className="mx-auto mb-8" data-id="element-156">
              {icon}
            </motion.div>}
          {label && <motion.p initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="text-gold font-bold tracking-wider uppercase mb-4" data-id="element-157">
              {label}
            </motion.p>}
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.15,
          duration: 0.6
        }} className="text-4xl md:text-6xl font-extrabold text-white mb-6 leading-tight" data-id="element-158">
            {title}
          </motion.h1>
          <motion.div initial={{
          opacity: 0,
          width: 0
        }} animate={{
          opacity: 1,
          width: '64px'
        }} transition={{
          delay: 0.35,
          duration: 0.8
        }} className="h-1 bg-gold mx-auto rounded-full mb-6" data-id="element-159" />
          {subtitle && <motion.p initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.45,
          duration: 0.5
        }} className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed" data-id="element-160">
              {subtitle}
            </motion.p>}
          {children && <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.6,
          duration: 0.5
        }} className="mt-8" data-id="element-161">
              {children}
            </motion.div>}
        </div>
      </section>;
  }
  // 'default' and 'centered' variants
  return <section className="relative pt-36 md:pt-44 pb-16 md:pb-24 bg-gradient-to-br from-warm to-white text-center px-4 overflow-hidden" data-id="element-162">
      <div className="absolute top-0 right-0 w-64 h-64 bg-gold/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-163" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-navy/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" data-id="element-164" />

      <div className="max-w-4xl mx-auto relative z-10" data-id="element-165">
        {label && <motion.p initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="text-gold font-bold tracking-wider uppercase mb-4" data-id="element-166">
            {label}
          </motion.p>}
        <motion.h1 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1,
        duration: 0.6
      }} className="text-4xl md:text-5xl font-extrabold text-navy mb-4 leading-tight" data-id="element-167">
          {title}
        </motion.h1>
        <motion.div initial={{
        opacity: 0,
        width: 0
      }} animate={{
        opacity: 1,
        width: '64px'
      }} transition={{
        delay: 0.3,
        duration: 0.8
      }} className="h-1 bg-gold mx-auto rounded-full mb-6" data-id="element-168" />
        {subtitle && <motion.p initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4,
        duration: 0.5
      }} className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed" data-id="element-169">
            {subtitle}
          </motion.p>}
        {children && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.55,
        duration: 0.5
      }} className="mt-8" data-id="element-170">
            {children}
          </motion.div>}
      </div>
    </section>;
}